import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed, Ref } from "vue"
import Modal from "@/components/widgets/common/Modal.vue"
import ExpansionPanels from "@/components/widgets/accordion/ExpansionPanels.vue"
import ExpansionPanel from "@/components/widgets/accordion/ExpansionPanel.vue"
import ExpansionHeader from "@/components/widgets/accordion/ExpansionHeader.vue"
import ExpansionContent from "@/components/widgets/accordion/ExpansionContent.vue"
import ExpandableTextBlock from "@/components/widgets/common/ExpandableTextBlock.vue"
import StateChip from "@/components/widgets/common/StateChip.vue"
import ActivityTimeline from "@/components/widgets/timeline/ActivityTimeline.vue"
import {
  MissionStaffingPositionsQuery,
  MissionStaffingPositionsQueryQuery,
  MissionStaffingPositionDetailFragmentFragment,
  MissionLifecycleFragmentFragment,
  GenerateConsultantPaymentStructureMutationMutation,
  GenerateConsultantPaymentStructureMutationMutationVariables,
  GenerateConsultantPaymentStructureMutation,
  ConsultantPaymentStructureInput,
  UpdateMissionExecutionStaffingPositionMutationMutation,
  UpdateMissionExecutionStaffingPositionMutationMutationVariables,
  UpdateMissionExecutionStaffingPositionMutation,
  MoneyInput,
  ConsultantContractPayment,
  CreateConsultantContractPaymentMutation,
  CreateConsultantContractPaymentMutationMutationVariables,
  CreateConsultantContractPaymentMutationMutation,
  UpdateConsultantContractPaymentMutation,
  UpdateConsultantContractPaymentMutationMutation,
  UpdateConsultantContractPaymentMutationMutationVariables,
} from "@/gql"
import { useQuery } from "@vue/apollo-composable"
import SetupPaymentForm from "@/modules/Missions/components/Payment/SetupPaymentForm.vue"
import { SetupPaymentType } from "@/modules/Missions/components/Payment/constant"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import ConsultantsApplications from "@/modules/Missions/components/Details/ManageTeamRoles/StaffingPositionDetails/ConsultantsApplications.vue"
import { ModalSize, modalSizes } from "@/components/widgets/common/Modal.vue"
import StaffingPositionActions from "@/modules/Missions/components/Details/ManageTeamRoles/StaffingPositionActions.vue"
import { Actions, Action } from "@/modules/Missions/components/Details/ManageTeamRoles/constant"
import { useStaffingPositionContext } from "./useStaffingPositionContext"
import { useUtilities } from "@/hooks/useUtilities"
import { PaymentFormValues } from "@/modules/Missions/components/Payment/CreatePaymentStructureForm.vue"
import ConsultantPaymentDetails from "@/modules/Missions/components/Details/ManageTeamRoles/StaffingPositionDetails/ConsultantPaymentDetails.vue"
import TeamRoleStateChip from "../TeamRoleStateChip.vue"

type ModalMode = (typeof modalModes)[keyof typeof modalModes]


export default /*#__PURE__*/_defineComponent({
  __name: 'StaffingPositionDetailsModal',
  props: {
    value: { type: Boolean },
    staffingPositionId: null,
    mission: null
  },
  emits: ["close"],
  setup(__props: any, { emit }) {

const props = __props as {
  value: boolean
  staffingPositionId: string
  mission: MissionLifecycleFragmentFragment
}

const modalModes = {
  DETAILS: "DETAILS",
  SETUP_PAYMENTS: "SETUP_PAYMENTS",
  PROFILE_VIEW: "PROFILE_VIEW",
  CONFIRM_APPLICATION_DIALOG: "CONFIRM_APPLICATION_DIALOG",
} as const





const { mutate } = useMutation()
const { addSuccess, addError } = useNotification()

const showDetailsModal = ref(false)
const staffingPositionAction = ref<Action | undefined>(undefined)

const modalMode = ref<ModalMode>(modalModes.DETAILS)

const modalWidth = computed<ModalSize>({
  get: () => {
    if (modalMode.value === modalModes.SETUP_PAYMENTS) return modalSizes.medium
    return modalSizes.large
  },
  set: (newWidth: ModalSize) => {
    modalWidth.value = newWidth
  },
})

const expansionPanelState = ref<Record<string, any>>({
  jobDescription: 0,
  deliverables: 0,
  applications: 1,
  activityHistory: 0,
})

const setupPaymentType = ref<(typeof SetupPaymentType)[keyof typeof SetupPaymentType]>(
  SetupPaymentType.Consultant
)

const activePayment = ref<ConsultantContractPayment | null>(null)

const addNewPayment = () => {
  setupPaymentType.value = SetupPaymentType.SinglePayment
  modalMode.value = modalModes.SETUP_PAYMENTS
}

const updatePayment = (payment: ConsultantContractPayment) => {
  activePayment.value = payment
  setupPaymentType.value = SetupPaymentType.SinglePayment
  modalMode.value = modalModes.SETUP_PAYMENTS
}

const onClose = () => {
  showDetailsModal.value = false
  emit("close")
}

watch(
  () => props.value,
  (newVal: boolean) => {
    showDetailsModal.value = newVal
  },
  { immediate: true }
)

const staffingPosition = ref<MissionStaffingPositionDetailFragmentFragment | null>(null)

const { loading, onResult } = useQuery<MissionStaffingPositionsQueryQuery>(
  MissionStaffingPositionsQuery,
  {
    filter: {
      id: props.staffingPositionId,
    },
  }
)

const refetchQueries = computed(() => {
  return [
    {
      query: MissionStaffingPositionsQuery,
      variables: {
        filter: {
          id: props.staffingPositionId,
        },
      },
    },
  ]
})

onResult((result) => {
  staffingPosition.value = result.data?.missionStaffingPositions.data[0]
})

const { canPublishApplication } = useStaffingPositionContext(
  staffingPosition as Ref<MissionStaffingPositionDetailFragmentFragment>
)

const isSubmitting = ref(false)

const onGenerateConsultantPaymentStructure = async (
  payments: ConsultantPaymentStructureInput[]
) => {
  if (!staffingPosition.value?.prn) return
  isSubmitting.value = true
  const result = await mutate<
    GenerateConsultantPaymentStructureMutationMutation,
    GenerateConsultantPaymentStructureMutationMutationVariables
  >({
    mutation: GenerateConsultantPaymentStructureMutation,
    variables: {
      missionStaffingPositionId: staffingPosition.value?.prn,
      paymentsInputs: payments,
    },
    done: () => {
      isSubmitting.value = false
    },
  })
  if (result?.data && !result?.data?.generateConsultantPaymentStructure.error) {
    addSuccess("Consultant fees created successfully")
    modalMode.value = modalModes.DETAILS
  }
}

const updateConsultantFeesMutation = async (totalFee: MoneyInput, callback?: () => void) => {
  if (!staffingPosition.value?.id) return

  isSubmitting.value = true

  const result = await mutate<
    UpdateMissionExecutionStaffingPositionMutationMutation,
    UpdateMissionExecutionStaffingPositionMutationMutationVariables
  >({
    mutation: UpdateMissionExecutionStaffingPositionMutation,
    variables: {
      id: staffingPosition.value.prn,
      name: staffingPosition.value.name,
      totalFee: totalFee as MoneyInput,
      description: staffingPosition.value.description,
    },
    done: () => {
      isSubmitting.value = false
    },
  })

  if (result?.data && !result?.data?.updateMissionExecutionStaffingPosition.error) {
    callback?.()
  }
}

const onCloseAction = () => {
  modalMode.value = modalModes.DETAILS
  staffingPositionAction.value = undefined
}

const onPublishApplication = () => {
  if (!canPublishApplication.value) {
    addError(
      "To publish this role please ensure the mission has a scope, start and end date have been set, and payments have been configured"
    )
    return
  }
  staffingPositionAction.value = Actions.Publish
}

const { pluralize } = useUtilities()

const pendingApplicationsLabel = computed(() =>
  pluralize(
    staffingPosition.value?.pendingMissionStaffingApplications.length || 0,
    "new application"
  )
)

const onSubmitPayment = async (payment: PaymentFormValues) => {
  if (activePayment.value) {
    await onUpdatePayment(payment)
  } else {
    await onAddNewPayment(payment)
  }
}

const onAddNewPayment = async (payment: PaymentFormValues) => {
  if (!staffingPosition.value?.prn) return
  isSubmitting.value = true
  const result = await mutate<
    CreateConsultantContractPaymentMutationMutation,
    CreateConsultantContractPaymentMutationMutationVariables
  >({
    mutation: CreateConsultantContractPaymentMutation,
    variables: {
      missionStaffingPositionId: staffingPosition.value?.prn,
      missionContractPaymentId: payment.clientPayment || "",
      consultantContractPaymentInput: {
        amount: payment.amount as MoneyInput,
        name: payment.name,
        description: payment.description,
        staffingPositionDeliverables: payment.deliverables,
      },
    },
    refetchQueries: refetchQueries.value,
    done: () => {
      isSubmitting.value = false
    },
  })
  if (result?.data && !result?.data?.createConsultantContractPayment.error) {
    addSuccess("Consultant fees created successfully")
    modalMode.value = modalModes.DETAILS
  }
}

const onUpdatePayment = async (payment: PaymentFormValues) => {
  if (!activePayment.value) return
  isSubmitting.value = true
  const result = await mutate<
    UpdateConsultantContractPaymentMutationMutation,
    UpdateConsultantContractPaymentMutationMutationVariables
  >({
    mutation: UpdateConsultantContractPaymentMutation,
    variables: {
      consultantContractPaymentId: activePayment.value.prn,
      consultantContractPaymentInput: {
        amount: payment.amount as MoneyInput,
        name: payment.name,
        description: payment.description,
        staffingPositionDeliverables: payment.deliverables,
      },
    },
    refetchQueries: refetchQueries.value,
    done: () => {
      isSubmitting.value = false
    },
  })
  if (result?.data && !result?.data?.updateConsultantContractPayment.error) {
    addSuccess("Consultant fees updated successfully")
    modalMode.value = modalModes.DETAILS
  }
}

return { __sfc: true,modalModes, props, emit, mutate, addSuccess, addError, showDetailsModal, staffingPositionAction, modalMode, modalWidth, expansionPanelState, setupPaymentType, activePayment, addNewPayment, updatePayment, onClose, staffingPosition, loading, onResult, refetchQueries, canPublishApplication, isSubmitting, onGenerateConsultantPaymentStructure, updateConsultantFeesMutation, onCloseAction, onPublishApplication, pluralize, pendingApplicationsLabel, onSubmitPayment, onAddNewPayment, onUpdatePayment, Modal, ExpansionPanels, ExpansionPanel, ExpansionHeader, ExpansionContent, ExpandableTextBlock, StateChip, ActivityTimeline, SetupPaymentForm, ConsultantsApplications, StaffingPositionActions, Actions, ConsultantPaymentDetails, TeamRoleStateChip }
}

})